import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@/hooks/useTheme';

export default function About() {
  const { colors } = useTheme();
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(entry.target); } },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { number: '50K+', label: 'Happy Customers', icon: 'ri-user-smile-fill' },
    { number: '30', label: 'Min Delivery', icon: 'ri-flashlight-fill' },
    { number: '4.8★', label: 'Customer Rating', icon: 'ri-star-fill' },
    { number: '1000+', label: 'Products Listed', icon: 'ri-t-shirt-line' },
  ];

  const features = [
    { icon: 'ri-truck-fill', text: '30-min delivery in Dehradun' },
    { icon: 'ri-ruler-line', text: 'Smart Size Exchange at doorstep' },
    { icon: 'ri-money-rupee-circle-fill', text: 'Cash on Delivery — pay at door' },
    { icon: 'ri-shield-check-fill', text: '100% genuine products guaranteed' },
  ];

  return (
    <section id="about" ref={sectionRef} className="relative py-16 md:py-24 overflow-hidden bg-white dark:bg-slate-900">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-slate-700 to-transparent"></div>

      {/* Background accent */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-[0.04]" style={{ background: 'radial-gradient(circle, #FF5C00, transparent)', transform: 'translate(40%, -40%)' }}></div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* Left */}
            <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <div 
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                style={{ backgroundColor: `${colors.primary}15`, borderColor: `${colors.primary}30`, border: '1px solid' }}
              >
                <span 
                  className="w-2 h-2 rounded-full animate-pulse"
                  style={{ backgroundColor: colors.primary }}
                ></span>
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: colors.primary }}>About Clothy Bazar</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 dark:text-white mb-5 leading-tight">
                Dehradun Ka #1<br />
                <span 
                  className="text-transparent bg-clip-text"
                  style={{ backgroundImage: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
                >Fashion Store</span>
              </h2>
              <p className="text-gray-600 dark:text-gray-300 text-base leading-relaxed mb-5">
                Clothy Bazar is Dehradun's premier fashion destination — bringing you the latest streetwear trends with lightning-fast <strong className="text-gray-900 dark:text-white">30-minute delivery</strong>. We believe premium fashion should be accessible, affordable, and at your doorstep instantly.
              </p>
              <p className="text-gray-600 dark:text-gray-300 text-base leading-relaxed mb-7">
                Our <strong className="text-gray-900 dark:text-white">Smart Size Delivery</strong> means you never have to worry about wrong size again — our delivery partner brings all sizes to your door so you can try before you keep.
              </p>

              {/* Feature list */}
              <div className="space-y-3 mb-8">
                {features.map((f, i) => (
                  <div key={i} className={`flex items-center gap-3 transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`} style={{ transitionDelay: `${i * 80 + 200}ms` }}>
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shrink-0">
                      <i className={`${f.icon} text-white text-sm`}></i>
                    </div>
                    <span className="text-gray-700 dark:text-gray-200 text-sm font-medium">{f.text}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <button className="px-6 py-3 rounded-xl font-bold text-sm text-white cursor-pointer hover:opacity-90 transition-all" style={{ background: 'linear-gradient(135deg, #FF5C00, #FF1744)' }}>
                  <i className="ri-store-2-line mr-2"></i>Shop Now
                </button>
                <button className="px-6 py-3 rounded-xl border-2 border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-200 font-bold text-sm hover:bg-gray-50 dark:hover:bg-slate-800 transition-all cursor-pointer whitespace-nowrap">
                  Our Story →
                </button>
              </div>
            </div>

            {/* Right — Stats Grid */}
            <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat, i) => (
                  <div
                    key={stat.label}
                    className={`relative overflow-hidden rounded-2xl p-6 border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-gray-200 dark:hover:border-slate-600 hover:-translate-y-1 transition-all duration-300 group ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                    style={{ transitionDelay: `${i * 100 + 300}ms` }}
                  >
                    <div className={`absolute top-0 right-0 w-16 h-16 rounded-full bg-gradient-to-br ${stat.color} opacity-10 -translate-y-4 translate-x-4`}></div>
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-4`}>
                      <i className={`${stat.icon} text-white text-lg`}></i>
                    </div>
                    <div className="text-3xl font-black text-gray-900 dark:text-white mb-1">{stat.number}</div>
                    <div className="text-gray-500 dark:text-gray-400 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Highlight card */}
              <div className="mt-4 rounded-2xl p-5 border border-orange-100 dark:border-orange-800/40 overflow-hidden relative dark:bg-orange-900/10" style={{ background: 'linear-gradient(135deg, #fff7ed, #fff1f2)' }}>
                <div className="absolute top-0 right-0 bottom-0 w-20 flex items-center justify-center opacity-[0.08]">
                  <i className="ri-shield-check-fill text-orange-500" style={{ fontSize: 80 }}></i>
                </div>
                <div className="relative z-10 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shrink-0">
                    <i className="ri-award-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-gray-900 dark:text-white font-black text-base mb-1">Trusted by 50,000+ Customers</h4>
                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">Dehradun's most loved fashion store with 4.8★ rating and 30-minute guaranteed delivery.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
